import React, { useEffect, useState } from 'react';
import { Cloud, Loader2 } from 'lucide-react';
import { apiClient } from '../../services/api';
import logger from '../../utils/logger';

interface WeatherData {
  temperature_celsius: number;
  humidity_percent: number;
  weather_description: string;
  weather_icon: string;
  city_name?: string;
}

export const HeaderWeatherWidget: React.FC = () => {
  const [weather, setWeather] = useState<WeatherData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchWeather = async () => {
      if (!navigator.geolocation) {
        setError('Geolocation not supported');
        return;
      }

      setLoading(true);
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          try {
            const { latitude, longitude } = position.coords;
            const data = await apiClient.get<WeatherData>('/weather/current', {
              params: { lat: latitude, lon: longitude },
            });
            setWeather(data);
          } catch (err) {
            logger.error('Failed to fetch header weather', { error: err as Error });
            setError('Failed to load');
          } finally {
            setLoading(false);
          }
        },
        (err) => {
          logger.warn('Geolocation denied/failed', { error: new Error(err.message) });
          setError('Location needed');
          setLoading(false);
        },
        { timeout: 10000, maximumAge: 600000 }, // Cache location for 10 min
      );
    };

    fetchWeather();

    // Refresh every 30 minutes
    const interval = setInterval(fetchWeather, 30 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  if (loading && !weather) {
    return (
      <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-full bg-gray-50/50 dark:bg-zinc-800/50 text-xs text-gray-400">
        <Loader2 className="w-3 h-3 animate-spin" />
        <span>Počasí...</span>
      </div>
    );
  }

  if (error || !weather) {
    return null; // Don't show anything on error/no permission to keep header clean
  }

  return (
    <div className="hidden sm:flex items-center gap-3 px-3 py-1.5 rounded-full bg-cyan-50/50 dark:bg-cyan-900/10 border border-cyan-100 dark:border-cyan-900/20 text-xs font-medium text-cyan-700 dark:text-cyan-300">
      <div className="flex items-center gap-1.5">
        {weather.weather_icon ? (
          <img
            src={`https://openweathermap.org/img/wn/${weather.weather_icon}.png`}
            alt={weather.weather_description}
            className="w-5 h-5 -my-1"
          />
        ) : (
          <Cloud className="w-3.5 h-3.5" />
        )}
        <span>{Math.round(weather.temperature_celsius)}°C</span>
      </div>
      {weather.city_name && weather.city_name !== 'Unknown' && (
        <>
          <div className="w-px h-3 bg-cyan-200 dark:bg-cyan-800" />
          <span className="truncate max-w-[80px]">{weather.city_name}</span>
        </>
      )}
    </div>
  );
};
